
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, first, map } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/shared/services/authentication/authentication.service';
import { DatePickerInfo } from '../../date-input/date-input.component';
import { AllowedFeatureEnum } from '../../shared/enums/allowed-feature.enum';
import { FileHelper } from '../../shared/helpers/file.helper';
import { ErrorApiModel } from '../../shared/models/api/error/error.apimodel';
import { ContratModel } from '../../shared/models/contrat.model';
import { CuisineCentraleModel } from '../../shared/models/cuisine-centrale.model';
import { FileResultModel } from '../../shared/models/fileresult.model';
import { NotificationModel } from '../../shared/models/notification.model';
import { RestaurantModel } from '../../shared/models/restaurant.model';
import { UserInfoModel } from '../../shared/models/user-info.model';
import { ApplicationDataService } from '../../shared/services/application-data.service';
import { InteractionService } from '../../shared/services/interaction.service';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { GrandCompteModel } from 'src/app/shared/models/grand-compte.model';
import { ReportingService } from 'src/app/reporting/services/reporting.service';
import { OdgReportingService } from '../services/odgReporting.service';
import { OdgPrestation, OdgPrestationsConvivesResponse } from '../models/api/responses/odg-prestations-convives.apiresponse';
import { StringHelper } from 'src/app/shared/helpers/string.helper';

interface PortailRestaurantPerimeter {
  grandCompteId?: number;
  cuisineCentraleId?: number;
  contratId?: number;
  dateDebut?: Date;
  dateFin?: Date;
}

@Component({
  selector: 'app-exports-odg',
  templateUrl: './exports-odg.component.html',
  styleUrls: ['./exports-odg.component.scss']
})
export class ExportsOdgComponent implements OnInit, OnDestroy {

  public dateCalendarData: DatePickerInfo = {
    minDate: moment().add(-2, 'year').startOf('year').toDate(),
    maxDate: moment().add(1, 'year').endOf('year').toDate()
  };

  public isLoading: boolean = false;
  public isLoadingOdgPrestationsConvives: boolean = false;
  public isDowloading: boolean = false;
  public isAllowed: boolean = false;

  public cuisines: CuisineCentraleModel[] = [];
  public grandComptes: GrandCompteModel[] = [];

  public allowGrandCompte: boolean = true;
  private _selectedGrandCompte: GrandCompteModel = null;
  public get selectedGrandCompte(): GrandCompteModel {
    return this._selectedGrandCompte;
  }
  public set selectedGrandCompte(v: GrandCompteModel) {
    if (this._selectedGrandCompte !== v) {
      this._selectedGrandCompte = v;
      if (this._selectedGrandCompte !== null) {
        this._selectedCuisine = null; // si grand compte alors toutes cuisines
      }
      const contrats = this.contrats; // on sélectionne le contrat si c'est le seul accessible
      this.selectedContrat = (contrats.length == 1)
        ? contrats[0] // seul contrat accessible sur le grand compte
        : null; // tous les contrats

      this.updatePerimeterState();
    }
  }

  private _selectedCuisine: CuisineCentraleModel = null;
  public get selectedCuisine(): CuisineCentraleModel {
    return this._selectedCuisine;
  }
  public set selectedCuisine(v: CuisineCentraleModel) {
    if (this._selectedCuisine !== v) {
      this._selectedCuisine = v;
      if (this._selectedCuisine !== null) {
        this._selectedGrandCompte = null; // si cuisine alors pas de filtre grand compte
      }
      const contrats = this.contrats; // on sélectionne le contrat si c'est le seul accessible
      this.selectedContrat = (contrats.length == 1)
        ? contrats[0] // seul contrat accessible sur la cuisine
        : null; // tous les contrats

      this.updatePerimeterState();
    }
  }

  public get contrats(): ContratModel[] {
    return (this.selectedCuisine && this.selectedCuisine.contrats)
      || (this.selectedGrandCompte && this.getGrandCompteContrats(this.selectedGrandCompte))
      || [];
  }

  private _selectedContrat: ContratModel = null;
  public get selectedContrat(): ContratModel {
    return this._selectedContrat;
  }
  public set selectedContrat(value: ContratModel) {
    this._selectedContrat = value;
    this.updatePerimeterState();
  }

  private _dateConsoDebut: Date = moment().startOf('month').toDate();
  public get dateConsoDebut(): Date {
    return this._dateConsoDebut;
  }
  public set dateConsoDebut(value: Date) {
    this._dateConsoDebut = value;
    this.updatePerimeterState();

  }
  private _dateConsoFin: Date = moment().endOf('month').startOf('day').toDate();
  public get dateConsoFin(): Date {
    return this._dateConsoFin;
  }
  public set dateConsoFin(value: Date) {
    this._dateConsoFin = value;
    this.updatePerimeterState();
  }

  private _perimeterState: BehaviorSubject<PortailRestaurantPerimeter> = new BehaviorSubject<PortailRestaurantPerimeter>({});

  private updatePerimeterState() {
    this._perimeterState.next({
      grandCompteId: this.selectedGrandCompte && this.selectedGrandCompte.id,
      cuisineCentraleId: this.selectedCuisine && this.selectedCuisine.id,
      contratId: this.selectedContrat && this.selectedContrat.id,
      dateDebut: this.dateConsoDebut,
      dateFin: this.dateConsoFin
    });
  }

  public prestationsConvives: OdgPrestationsConvivesResponse = null;
  private loadOdgPrestationsConvives(perimeter: PortailRestaurantPerimeter) {

    this.isLoadingOdgPrestationsConvives = true;
    this._odgReportingService.getPrestationsConvives(
      perimeter.grandCompteId,
      perimeter.cuisineCentraleId,
      perimeter.contratId,
      perimeter.dateDebut,
      perimeter.dateFin
    ).pipe(
      untilDestroyed(this),
      first()
    ).subscribe((response: OdgPrestationsConvivesResponse) => {
      this.prestationsConvives = response;

      //preselect prestations and convives
      //default prestation : DEJEUNER
      //default convive : Primaire / Maternelle
      if (this.selectedPrestations == null || this.selectedPrestations.length === 0) {
        this.selectedPrestations = this.prestationsConvives.prestations.filter(p => StringHelper.includes(p.libelle, 'DEJEUNER', false, true));
      } else {
        //try reselect the same prestations
        this.selectedPrestations = this.prestationsConvives.prestations.filter(p => this.selectedPrestations.some(sp => sp.id === p.id));
      }

      if (this.selectedConvives == null || this.selectedConvives.length === 0) {
        this.selectedConvives = this.prestationsConvives.convives.filter(c =>
          StringHelper.includes(c.libelle, 'Primaire', false, true)
          || StringHelper.includes(c.libelle, 'Maternelle', false, true)
          || StringHelper.includes(c.libelle, 'Elementaire', false, true)
        );
      } else {
        //try reselect the same convives
        this.selectedConvives = this.prestationsConvives.convives.filter(c => this.selectedConvives.some(sc => sc.id === c.id));
      }

      this.isLoadingOdgPrestationsConvives = false;

      this._cd.markForCheck();
    });

  }

  public selectedPrestations?: OdgPrestation[] = null;
  public selectedConvives?: OdgPrestation[] = null;

  constructor(
    private _appDataService: ApplicationDataService,
    private _authService: AuthenticationService,
    private _reportingService: ReportingService,
    private _odgReportingService: OdgReportingService,
    private _cd: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.isAllowed = false;
    this.isLoading = true;
    // on prends le dernier restaurant et on complete le stream que l'on combine ensuite aux infos user pour en tirer le context actuel
    combineLatest([
      this._appDataService.restaurant$,
      this._authService.currentUser$]
    )
      .pipe(
        untilDestroyed(this),
        map(([restaurant, user]) => ({ restaurant, user }))
      )
      .subscribe((result: { restaurant: RestaurantModel, user: UserInfoModel }) => {
        this.cuisines = result && result.user && result.user.cuisines || [];
        this.grandComptes = result && result.user && result.user.grandComptes || [];
        // on affiche les grand compte que s'il y en a plusieurs (null compris)
        this.allowGrandCompte = result && result.user && new Set(result.user.allContrats.map(c => c.grandCompteId)).size > 1;

        this.selectedCuisine = result && result.user && result.restaurant && this.cuisines.find(c => c.id === result.restaurant.cuisineCentraleId);
        this.selectedContrat = result && result.user && result.restaurant && this.contrats.find(c => c.id === result.restaurant.contratId);
        this.isAllowed = result && result.user && result.user.hasAccessToFeature(AllowedFeatureEnum.ObservatoireDuGout);

        this.isLoading = false;
      });

    this._perimeterState.pipe(
      untilDestroyed(this),
      debounceTime(500),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ).subscribe((p) => {
      this.loadOdgPrestationsConvives(p);
    });
  }

  ngOnDestroy(): void {
  }

  exportRecapitulatifDesPlats() {
    this.isDowloading = true;
    const report$ = this._odgReportingService
      .exportRecapitulatifDesPlats(
        this.selectedGrandCompte && this.selectedGrandCompte.id,
        this.selectedCuisine && this.selectedCuisine.id,
        this.selectedContrat && this.selectedContrat.id,
        this.dateConsoDebut,
        this.dateConsoFin,
        this.selectedPrestations && this.selectedPrestations.map(p => p.id),
        this.selectedConvives && this.selectedConvives.map(c => c.id)
      ).pipe(
        debounceTime(1000),
        first(),
        finalize(() => {
          this.isDowloading = false;
          this._cd.markForCheck();
        }));

    this._reportingService.handleReportResponse(report$);
  }

  exportSyntheseDetailleePlats() {
    this.isDowloading = true;
    const report$ = this._odgReportingService
      .exportSyntheseDetailleePlats(
        this.selectedGrandCompte && this.selectedGrandCompte.id,
        this.selectedCuisine && this.selectedCuisine.id,
        this.selectedContrat && this.selectedContrat.id,
        this.dateConsoDebut,
        this.dateConsoFin,
        this.selectedPrestations && this.selectedPrestations.map(p => p.id),
        this.selectedConvives && this.selectedConvives.map(c => c.id)
      ).pipe(
        debounceTime(1000),
        first(),
        finalize(() => {
          this.isDowloading = false;
          this._cd.markForCheck();
        }));

    this._reportingService.handleReportResponse(report$);
  }

  exportSyntheseObservations() {
    this.isDowloading = true;
    const report$ = this._odgReportingService
      .exportSyntheseObservations(
        this.selectedGrandCompte && this.selectedGrandCompte.id,
        this.selectedCuisine && this.selectedCuisine.id,
        this.selectedContrat && this.selectedContrat.id,
        this.dateConsoDebut,
        this.dateConsoFin,
        this.selectedPrestations && this.selectedPrestations.map(p => p.id),
        this.selectedConvives && this.selectedConvives.map(c => c.id)
      ).pipe(
        debounceTime(1000),
        first(),
        finalize(() => {
          this.isDowloading = false;
          this._cd.markForCheck();
        }));

    this._reportingService.handleReportResponse(report$);
  }

  exportSyntheseGlobale() {
    this.isDowloading = true;
    const report$ = this._odgReportingService
      .exportSyntheseGlobale(
        this.selectedGrandCompte && this.selectedGrandCompte.id,
        this.selectedCuisine && this.selectedCuisine.id,
        this.selectedContrat && this.selectedContrat.id,
        this.dateConsoDebut,
        this.dateConsoFin,
        this.selectedPrestations && this.selectedPrestations.map(p => p.id),
        this.selectedConvives && this.selectedConvives.map(c => c.id)
      ).pipe(
        debounceTime(1000),
        first(),
        finalize(() => {
          this.isDowloading = false;
          this._cd.markForCheck();
        }));

    this._reportingService.handleReportResponse(report$);
  }

  private getGrandCompteContrats(grandCompte: GrandCompteModel): ContratModel[] {
    return this.cuisines
      && this.cuisines.reduce<ContratModel[]>((prev, current) => prev.concat(grandCompte == null ? current.contrats : current.contrats.filter(c => c.grandCompteId === grandCompte.id)), []) || [];
  }

}

